import React, { createContext, useState, useContext, useEffect } from 'react';
import { getProjects } from '../services/api';
import ProjectSelectDialog from '../components/ProjectSelectDialog';
import { useAuth } from './AuthContext';

const ProjectContext = createContext();

export const ProjectProvider = ({ children }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const { user, backendError, updateUser } = useAuth();

  // Reset state when user logs out
  useEffect(() => {
    if (!user) {
      setSelectedProject(null);
      setProjects([]);
      setShowProjectDialog(false);
    }
  }, [user]);

  // Load projects when user is authenticated
  useEffect(() => {
    if (user?.firebaseUser?.emailVerified) {
      loadProjects();
    }
  }, [user?.firebaseUser?.emailVerified]);

  // Handle project selection after projects are loaded
  useEffect(() => {
    if (!user?.firebaseUser?.emailVerified || projects.length === 0) return;

    // If there's only one project, select it automatically
    if (projects.length === 1) {
      handleProjectSelect(projects[0]);
      return;
    }

    // Otherwise, try to load saved project or show dialog
    const savedProjectId = localStorage.getItem('selectedProjectId');
    if (savedProjectId) {
      const project = projects.find(p => p.id === parseInt(savedProjectId));
      if (project) {
        setSelectedProject(project);
      } else {
        setShowProjectDialog(true);
      }
    } else {
      setShowProjectDialog(true);
    }
  }, [projects, user?.firebaseUser?.emailVerified]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
      // Update user with backend error if it's a network error
      if (error.message === 'Network Error') {
        updateUser(prev => ({
          ...prev,
          is_onboarded: undefined // Reset onboarding status on network error
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setShowProjectDialog(false);
    if (project) {
      localStorage.setItem('selectedProjectId', project.id.toString());
    } else {
      localStorage.removeItem('selectedProjectId');
      setShowProjectDialog(true);
    }
  };

  const value = {
    selectedProject,
    setSelectedProject: handleProjectSelect,
    projects,
    loading,
    reloadProjects: loadProjects,
    openProjectDialog: () => setShowProjectDialog(true),
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
      <ProjectSelectDialog
        open={showProjectDialog && user?.firebaseUser?.emailVerified && !backendError}
        onClose={() => setShowProjectDialog(false)}
        projects={projects}
        onSelect={handleProjectSelect}
      />
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}; 